/**
 * <drip-sale-popup>
 * Sitewide email-capture popup. Shows once after a configurable delay,
 * remembers dismissal per the merchant-chosen frequency, and is fully
 * reusable in the theme editor (always opens while its section is selected).
 *
 * There is exactly one modal and one job: collect the email. The signup form
 * submits over fetch() so nothing reloads; on success the email field swaps
 * for a short "thanks" line in the same modal, which then closes itself
 * shortly after. If JS fails to load, the form still posts normally and the
 * server-rendered version of that same swap (see drip-sale-popup.liquid)
 * shows the same result — no discount code, no second screen either way.
 */
class DripSalePopup extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.drip-popup-overlay');
    this.dialog = this.querySelector('.drip-popup');
    this.form = this.closest('form');
    this.storageKey = this.dataset.storageKey || 'drip-sale-popup';
    this.frequency = this.dataset.frequency || 'session';
    this.delayMs = Number(this.dataset.delayMs) || 0;
    this.forceOpen = this.dataset.forceOpen === 'true';
    this.onKeydown = this.onKeydown.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    if (!this.dialog) return;

    this.querySelectorAll('.drip-popup__close, .drip-popup__dismiss').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    if (this.form) this.form.addEventListener('submit', this.onSubmit);

    // The customer signup form just posted back to this page (success or
    // validation error) — show the result immediately, skip the delay and
    // any "already dismissed" check.
    if (this.forceOpen) {
      this.open();
      // Strip Shopify's "?customer_posted=true" from the address bar so a
      // plain reload/share of this URL doesn't keep landing on the "thanks"
      // screen — a fresh page load should always start at the email step.
      this.cleanRedirectUrl();
      // No-JS fallback landed here already showing the server-rendered
      // "thanks" line (see drip-sale-popup.liquid) — close it the same way
      // the AJAX path does.
      this.scheduleAutoClose();
      return;
    }

    // Theme editor: let the merchant preview the popup by selecting the section,
    // and don't let localStorage dismissal hide it from them while editing.
    if (Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.onSectionLoad.bind(this));
      document.addEventListener('shopify:section:select', this.onSectionSelect.bind(this));
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect.bind(this));
      return;
    }

    if (this.wasDismissedRecently()) return;

    this.timer = window.setTimeout(() => this.open(), this.delayMs);
  }

  cleanRedirectUrl() {
    if (!window.history || !window.history.replaceState) return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('customer_posted')) {
        url.searchParams.delete('customer_posted');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
    } catch (error) {
      // Ignore — worst case the query param just stays visible in the address bar.
    }
  }

  onSectionLoad(event) {
    if (event.target.contains(this)) this.open();
  }

  onSectionSelect(event) {
    if (event.target.contains(this)) this.open();
  }

  onSectionDeselect(event) {
    if (event.target.contains(this)) this.close(false);
  }

  wasDismissedRecently() {
    if (this.frequency === 'always') return false;

    try {
      if (this.frequency === 'session') {
        return sessionStorage.getItem(this.storageKey) === '1';
      }

      const dismissedAt = Number(localStorage.getItem(this.storageKey));
      if (!dismissedAt) return false;

      const windowMs = this.frequency === 'week' ? 7 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
      return Date.now() - dismissedAt < windowMs;
    } catch (error) {
      // Storage unavailable (private mode, disabled cookies, etc.) — always show.
      return false;
    }
  }

  rememberDismissal() {
    try {
      if (this.frequency === 'session') {
        sessionStorage.setItem(this.storageKey, '1');
      } else if (this.frequency !== 'always') {
        localStorage.setItem(this.storageKey, String(Date.now()));
      }
    } catch (error) {
      // Ignore storage failures — the popup just shows again next time.
    }
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.overlay.hidden = false;
    this.dialog.hidden = false;
    // Force layout so the transition below plays instead of the elements just appearing.
    this.overlay.offsetHeight;

    this.overlay.classList.add('is-open');
    this.dialog.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    document.addEventListener('keydown', this.onKeydown);
    this.overlay.addEventListener('click', this.onOverlayClick);

    if (typeof trapFocus === 'function') {
      trapFocus(this.dialog, this.dialog.querySelector('.drip-popup__close'));
    } else {
      const closeButton = this.dialog.querySelector('.drip-popup__close');
      if (closeButton) closeButton.focus();
    }
  }

  close(remember = true) {
    if (!this.isOpen) return;
    this.isOpen = false;

    if (remember) this.rememberDismissal();

    this.overlay.classList.remove('is-open');
    this.dialog.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');

    document.removeEventListener('keydown', this.onKeydown);
    this.overlay.removeEventListener('click', this.onOverlayClick);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }

    window.setTimeout(() => {
      if (this.isOpen) return;
      this.overlay.hidden = true;
      this.dialog.hidden = true;
    }, 250);
  }

  onKeydown(event) {
    if (event.code === 'Escape') this.close();
  }

  // Reaching here means the browser already ran its own constraint
  // validation (required/type=email) — an invalid submission never fires
  // a "submit" event at all, so there's nothing extra to check first.
  async onSubmit(event) {
    const formEl = this.querySelector('.drip-popup__form');
    if (formEl && formEl.hidden) return; // already submitted successfully

    event.preventDefault();

    const submitButton = this.form.querySelector('.drip-popup__submit[type="submit"]');
    if (submitButton) submitButton.disabled = true;

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
      });

      // Shopify's customer-signup endpoint doesn't hand back a simple JSON
      // success flag over fetch, and a normal signup essentially never
      // fails server-side once the email has passed the browser's own
      // validation — so any response that completes without a network
      // error is treated as a successful signup.
      if (response.ok) {
        this.showSuccess();
      } else {
        this.fallbackToNativeSubmit();
      }
    } catch (error) {
      // fetch() itself failed (network hiccup, storefront blocking the
      // request in some environments, etc.) — rather than telling a real
      // visitor their signup failed, fall back to a normal form submission
      // so the email still gets captured the reliable, original way.
      this.fallbackToNativeSubmit();
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  }

  fallbackToNativeSubmit() {
    this.form.submit();
  }

  showSuccess() {
    const formEl = this.querySelector('.drip-popup__form');
    const thanksEl = this.querySelector('.drip-popup__thanks');
    if (formEl) formEl.hidden = true;
    if (thanksEl) thanksEl.hidden = false;
    this.scheduleAutoClose();
  }

  scheduleAutoClose() {
    const thanksEl = this.querySelector('.drip-popup__thanks');
    if (!thanksEl || thanksEl.hidden) return;
    window.setTimeout(() => this.close(), 1800);
  }

  onOverlayClick() {
    this.close();
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    document.removeEventListener('keydown', this.onKeydown);
  }
}

customElements.define('drip-sale-popup', DripSalePopup);
